import React from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import CookieBlockedIframe from '../CookieBlockedIframe/CookieBlockedIframe';
import infographic from './infographic_problem1230_600.jpg';
import infographic2 from './infographic_stats_2025_1200_630.jpg';

import css from './FixDeafAccessContent.module.css';

const FixDeafAccessContent = props => {
  const { rootClassName = null, className = null } = props;

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={`${classes} ${css.staticPageWrapper}`}>
      <div className={css.contentWrapper}>
        <nav className={css.tableOfContents}>
          <ul>
            <li><a href="#the-problem">The Problem</a></li>
            <li><a href="#our-approach">Our Approach</a></li>
            <li><a href="#our-cooperative">Our Co-operative</a></li>
            <li><a href="#our-impact">Our Impact</a></li>
            <li><a href="#support-signalise">Support Signalise</a></li>
          </ul>
        </nav>

        <section className={css.contentSection}>
          <h2 id="the-problem" className={css.title}>The Problem</h2>
          <p>Large spoken language agency contracts result in:</p>
          <ul className={css.list}>
            <li>Missed appointments, no-shows and last-minute cancellations</li>
            <li>Poor communication and user-friendly systems</li>
            <li>Interpreters and Deaf people excluded from bookings</li>
          </ul>
          <p>See reports by <a href="https://signhealth.org.uk/resources/still-ignored-the-fight-for-accessible-healthcare/" target="_blank" rel="noopener noreferrer">RNID/SignHealth</a> and the <a href="https://www.gov.uk/government/publications/bsl-user-experience-of-health-and-social-care-in-uk/locked-out-exclusion-of-deaf-and-deafblind-bsl-users-from-health-and-social-care-in-the-uk-full-report-bsl-and-english-versions" target="_blank" rel="noopener noreferrer">BSL Advisory Board</a>.</p>
          <img className={css.infographic} src={infographic} alt="Infographic showing the problems with deaf access" />
        </section>

        <section className={css.contentSection}>
          <h2 id="our-approach" className={css.title}>Our Approach</h2>
          <ul className={css.list}>
            <li><strong>Deaf-Led & Accessible:</strong> Guided by the lived experience of those we serve.</li>
            <li><strong>Community Owned:</strong> Built, governed, and sustained by the British Deaf community.</li>
            <li><strong>Innovation-First:</strong> Redefining interpreting services through modern, agile solutions.</li>
          </ul>
          <p>We don't just deliver BSL services; we invest in the social fabric of the Deaf community. Watch the video below to see how our unique approach drives genuine public sector transformation.</p>
          <div className={css.videoSection}>
            <div className={css.videoContainer}>
              <CookieBlockedIframe
                src="https://www.youtube-nocookie.com/embed/9dCu4RcLUlU?rel=0&origin=https://signalise.coop"
                dataCookieConsent="marketing"
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="embedded YouTube animation titled Why Signalise"
              />
            </div>
          </div>
        </section>

        <section className={css.contentSection}>
          <h2 id="our-cooperative" className={css.title}>Our Co-operative</h2>
          <ul className={css.list}>
            <li><strong>Who owns it:</strong> We are 100% owned and governed by the British Deaf community.</li>
            <li><strong>How decisions are made:</strong> Our strategy is shaped through democratic participation, ensuring lived experience leads every choice.</li>
            <li><strong>Where money goes:</strong> Surplus is reinvested into community initiatives and social innovation rather than private shareholders.</li>
          </ul>
          <p>By keeping ownership and decision-making within the community, we ensure that every penny of profit is reinvested back into the people and projects that empower the British Deaf community.</p>
          <div className={css.videoSection}>
            <div className={css.videoContainer}>
              <CookieBlockedIframe
                src="https://www.youtube-nocookie.com/embed/_GN817bfgHM?rel=0&origin=https://signalise.coop"
                dataCookieConsent="marketing"
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="embedded YouTube animation titled Why Signalise"
              />
            </div>
          </div>
        </section>

        <section className={css.contentSection}>
          <h2 id="our-impact" className={css.title}>Our Impact</h2>
          <ul className={css.list}>
            <li><a href="/our-services#contracts-and-work">Frameworks</a> - We are now on national and regional frameworks and can deliver better value for public sector organisations.</li>
            <li><a href="/p/social-impact">Social impact</a> - We provide more than just BSL services; we bridge the gap between public sector innovation and the lived experience of the Deaf community.</li>
            <li>Fulfilment rates - see image below for our unbelievable stats from 2025</li>
          </ul>
          <img className={css.infographic} src={infographic2} alt="Infographic showing our great stats from 2025" />
        </section>

         <section className={css.contentSection}>
          <h2 id="support-signalise" className={css.title}>Support Signalise</h2>
          <p>Signalise is a <strong>community-owned social enterprise</strong> powered by the people we serve. We invite you to be part of this change—whether you are a community member looking for ownership or a commissioner/buyer looking for great services with industry-leading social value.</p><p className={css.subtitle}>Choose your path to help us redefine Deaf-led services.</p>

          <div className={css.ctaGrid}>
            {/* For Deaf People */}
            <div className={css.card}>
              <h3>For the Deaf Community</h3>
              <p>Take ownership of your services and have your say.</p>
              <a href="/join-us-deaf-community" className={css.buttonPrimary}>Join Us Today</a>
              <p>Are you a Deaf organisation who wants to fix access for local Deaf people?</p>
              <p>Get in touch and email us at <a href="mailto:hello@signalise.coop">hello@signalise.coop</a></p>
              
            </div>

            {/* For Professionals */}
            <div className={css.card}>
              <h3>For Interpreters & Professionals</h3>
              <p>Work with a co-operative that values your expertise.</p>
              <a href="/join-us-communication-professionals" className={css.buttonPrimary}>Join and Work With Us</a>
            </div>

            {/* For Partners */}
            <div className={css.card}>
              <h3>For Organisations & Commissioners</h3>
              <p>Access high-quality, ethical, and innovative BSL services.</p>
              <a href="/our-services" className={css.buttonPrimary}>Explore Our Services</a>
            </div>

            {/* Investing */}
            <div className={css.cardHighlight}>
              <h3>Invest in our Future</h3>
              <p>Help us scale our impact and grow our Deaf-led model.</p>
              <a href="/p/invest" className={css.buttonPrimary}>Invest in Signalise</a>
            </div>
          </div>

          <div className={css.extraLinks}>
            <h3>Other Ways to Connect</h3>
            <p>
              Need remote access? View our <a href="/video-interpreting-service">VRS & VRI Services</a>. <br />
              Want to give your time? <a href="/get-involved">Volunteer or Get Involved</a>.
            </p>
          </div>

        </section>

      </div>
    </div>
  );
};

FixDeafAccessContent.propTypes = {
  rootClassName: string,
  className: string,
};

export default FixDeafAccessContent;
